/**
 * Low-poly creatures built from primitives, like everything else. Each returns named parts
 * so ActorView can animate them: legs swing, wings flap, the body lunges when it attacks.
 * Colours come from the creature table, so re-skinning a creature is a data edit.
 */
import * as THREE from 'three';
import type { CreatureDef } from '@sunderfen/shared';
import { G, addMesh, mat, mergeRig } from './models.js';

export interface CreatureModel {
  root: THREE.Group;
  /** Everything above the legs; lunges and bobs. */
  body: THREE.Group;
  legs: THREE.Group[];
  wings: THREE.Group[];
  /** Floating creatures hover instead of walking. */
  floats: boolean;
  /** Height of the name tag. */
  tagY: number;
}

/** Creature parts are soft: every box gets a generous bevel. */
const B = (w: number, h: number, d: number) => G.round(w, h, d, Math.min(w, h, d) * 0.32);
const limb = (parent: THREE.Object3D, x: number, y: number, z: number) => { const g = new THREE.Group(); g.position.set(x, y, z); parent.add(g); return g; };
const eye = (g: THREE.Object3D, x: number, y: number, z: number, glow?: number) => {
  const m = new THREE.Mesh(G.box(0.05, 0.05, 0.03), glow ? new THREE.MeshBasicMaterial({ color: glow }) : mat(0x111111));
  m.position.set(x, y, z); g.add(m);
};

export function buildCreature(d: CreatureDef): CreatureModel {
  const root = new THREE.Group(), body = new THREE.Group();
  root.add(body);
  const legs: THREE.Group[] = [], wings: THREE.Group[] = [];
  const c = d.color, a = d.accent ?? 0x333333;
  let floats = false, tagY = 1.4;
  const quad = (lx: number, lz: number, ly: number, lw: number, lh: number, col: number) => {
    for (const [x, z] of [[-lx, lz], [lx, lz], [-lx, -lz], [lx, -lz]]) { const l = limb(root, x, ly, z); addMesh(l, B(lw, lh, lw), col, 0, -lh / 2, 0); legs.push(l); }
  };
  switch (d.model) {
    case 'chicken': {
      addMesh(body, B(0.34, 0.3, 0.44), c, 0, 0.36, 0);
      addMesh(body, B(0.22, 0.24, 0.2), c, 0, 0.6, 0.18);
      addMesh(body, B(0.06, 0.1, 0.1), a, 0, 0.76, 0.18);
      addMesh(body, B(0.08, 0.06, 0.1), 0xe8a030, 0, 0.58, 0.32);
      addMesh(body, B(0.2, 0.2, 0.08), c, 0, 0.46, -0.24);
      eye(body, -0.08, 0.64, 0.28); eye(body, 0.08, 0.64, 0.28);
      for (const s of [-1, 1]) { const w = limb(body, s * 0.18, 0.42, 0); addMesh(w, B(0.04, 0.2, 0.3), c, s * 0.02, -0.06, 0); wings.push(w); }
      for (const s of [-1, 1]) { const l = limb(root, s * 0.08, 0.22, 0); addMesh(l, B(0.04, 0.22, 0.04), 0xe8a030, 0, -0.11, 0); legs.push(l); }
      tagY = 0.9; break;
    }
    case 'cow': case 'sheep': case 'boar': {
      const big = d.model === 'cow' ? 1 : d.model === 'boar' ? 0.8 : 0.85;
      const w = 0.62 * big, h = 0.5 * big, len = 1.1 * big;
      addMesh(body, B(w, h, len), c, 0, 0.62 * big + 0.1, 0);
      if (d.model === 'sheep') { addMesh(body, B(w + 0.1, h + 0.08, len - 0.1), 0xf8f6f0, 0, 0.66 * big + 0.12, 0); for (let i = 0; i < 9; i++) addMesh(body, G.ico(0.17), 0xfbfaf4, ((i % 3) - 1) * w * 0.4, 0.66 * big + 0.12 + h * 0.42, ((Math.floor(i / 3)) - 1) * len * 0.3); }
      if (d.model === 'cow') for (const [x, z] of [[0.2, 0.2], [-0.15, -0.25], [0.1, -0.1]]) addMesh(body, B(0.02 + w / 2.2, 0.2, 0.26), a, x, 0.82, z).scale.set(1, 0.6, 1);
      const head = limb(body, 0, 0.72 * big + 0.1, len / 2 + 0.12);
      addMesh(head, B(0.36 * big, 0.34 * big, 0.36 * big), d.model === 'sheep' ? a : c, 0, 0, 0);
      if (d.model === 'cow') { addMesh(head, B(0.3, 0.16, 0.12), 0xe8b8a8, 0, -0.08, 0.2); for (const s of [-1, 1]) addMesh(head, G.cone(0.04, 0.16, 5), 0xefe6d0, s * 0.15, 0.22, 0); }
      if (d.model === 'boar') { addMesh(head, B(0.18, 0.14, 0.16), 0x8a6a5a, 0, -0.06, 0.24); for (const s of [-1, 1]) { const t = addMesh(head, G.cone(0.03, 0.18, 4), a, s * 0.1, -0.02, 0.28); t.rotation.x = -1.1; } addMesh(body, B(0.1, 0.18, len * 0.8), 0x3a2a20, 0, 0.62 * big + 0.1 + h / 2, 0); }
      eye(head, -0.1 * big, 0.06, 0.18 * big + 0.01); eye(head, 0.1 * big, 0.06, 0.18 * big + 0.01);
      quad(w / 2 - 0.08, len / 2 - 0.14, 0.42 * big + 0.1, 0.14 * big, 0.44 * big + 0.1, d.model === 'sheep' ? a : c);
      tagY = 1.5 * big + 0.3; break;
    }
    case 'rabbit': {
      addMesh(body, B(0.26, 0.24, 0.4), c, 0, 0.24, 0);
      addMesh(body, B(0.2, 0.2, 0.2), c, 0, 0.36, 0.2);
      for (const s of [-1, 1]) addMesh(body, B(0.05, 0.24, 0.07), c, s * 0.06, 0.56, 0.18);
      addMesh(body, B(0.1, 0.1, 0.08), a, 0, 0.28, -0.22);
      eye(body, -0.07, 0.4, 0.3); eye(body, 0.07, 0.4, 0.3);
      quad(0.08, 0.12, 0.14, 0.07, 0.14, c);
      tagY = 0.8; break;
    }
    case 'wolf': {
      addMesh(body, B(0.38, 0.4, 0.9), c, 0, 0.66, 0);
      addMesh(body, B(0.4, 0.3, 0.3), a, 0, 0.6, 0.26);
      const head = limb(body, 0, 0.82, 0.52);
      addMesh(head, B(0.3, 0.28, 0.3), c, 0, 0, 0);
      addMesh(head, B(0.16, 0.14, 0.2), c, 0, -0.06, 0.22);
      for (const s of [-1, 1]) addMesh(head, G.cone(0.06, 0.14, 4), c, s * 0.1, 0.2, -0.02);
      eye(head, -0.08, 0.06, 0.16, 0xffd060); eye(head, 0.08, 0.06, 0.16, 0xffd060);
      const tail = addMesh(body, B(0.1, 0.1, 0.4), c, 0, 0.74, -0.6); tail.rotation.x = 0.5;
      quad(0.13, 0.32, 0.48, 0.1, 0.48, c);
      tagY = 1.4; break;
    }
    case 'frog': {
      addMesh(body, B(0.7, 0.4, 0.7), c, 0, 0.3, 0);
      addMesh(body, B(0.6, 0.16, 0.5), a, 0, 0.16, 0.06);
      for (const s of [-1, 1]) { addMesh(body, B(0.16, 0.16, 0.16), c, s * 0.22, 0.56, 0.22); eye(body, s * 0.22, 0.6, 0.31); }
      for (const s of [-1, 1]) { const l = limb(root, s * 0.38, 0.2, -0.15); addMesh(l, B(0.18, 0.18, 0.4), c, 0, -0.08, 0); legs.push(l); const f = limb(root, s * 0.26, 0.2, 0.3); addMesh(f, B(0.08, 0.2, 0.08), c, 0, -0.1, 0); legs.push(f); }
      tagY = 1.1; break;
    }
    case 'lurker': {
      addMesh(body, G.dod(0.7), c, 0, 0.6, 0).scale.set(1.2, 0.8, 1);
      for (let i = 0; i < 6; i++) { const r = addMesh(body, G.cone(0.08, 0.5, 4), 0x2a3a1a, Math.cos(i) * 0.5, 0.9 + (i % 2) * 0.1, Math.sin(i) * 0.4); r.rotation.z = Math.cos(i) * 0.5; }
      for (const s of [-1, 1]) eye(body, s * 0.2, 0.8, 0.6, a);
      for (const s of [-1, 1]) { const arm = limb(body, s * 0.8, 0.7, 0.2); addMesh(arm, B(0.22, 0.7, 0.22), c, 0, -0.3, 0); wings.push(arm); }
      floats = true; tagY = 1.8; break;
    }
    case 'bat': {
      addMesh(body, B(0.22, 0.26, 0.26), c, 0, 0, 0);
      for (const s of [-1, 1]) addMesh(body, G.cone(0.05, 0.12, 4), c, s * 0.07, 0.18, 0);
      eye(body, -0.05, 0.04, 0.13, a); eye(body, 0.05, 0.04, 0.13, a);
      for (const s of [-1, 1]) { const w = limb(body, s * 0.1, 0.02, 0); addMesh(w, B(0.46, 0.02, 0.3), c, s * 0.23, 0, 0); wings.push(w); }
      body.position.y = 1.1; floats = true; tagY = 1.6; break;
    }
    case 'goblin': {
      for (const s of [-1, 1]) { const l = limb(root, s * 0.1, 0.42, 0); addMesh(l, B(0.13, 0.42, 0.15), a, 0, -0.2, 0); legs.push(l); }
      addMesh(body, B(0.42, 0.44, 0.3), a, 0, 0.66, 0);
      addMesh(body, B(0.36, 0.34, 0.32), c, 0, 1.04, 0.02);
      for (const s of [-1, 1]) { const e = addMesh(body, G.cone(0.07, 0.3, 4), c, s * 0.26, 1.1, 0); e.rotation.z = -s * 1.3; }
      addMesh(body, B(0.1, 0.1, 0.1), c, 0, 0.98, 0.2);
      eye(body, -0.08, 1.08, 0.18, 0xffe040); eye(body, 0.08, 1.08, 0.18, 0xffe040);
      addMesh(body, G.cyl(0.2, 0.22, 0.1, 8), 0x8a7a50, 0, 1.24, 0);
      for (const s of [-1, 1]) { const arm = limb(body, s * 0.27, 0.84, 0); addMesh(arm, B(0.1, 0.36, 0.1), c, 0, -0.16, 0); wings.push(arm); }
      tagY = 1.6; break;
    }
    case 'wraith': {
      const ghost = new THREE.MeshLambertMaterial({ color: c, transparent: true, opacity: 0.72, emissive: new THREE.Color(d.glow ?? c).multiplyScalar(0.35) });
      const m1 = new THREE.Mesh(G.cone(0.42, 1.4, 6), ghost); m1.position.y = 0.9; m1.rotation.x = Math.PI; body.add(m1);
      const hood = new THREE.Mesh(B(0.4, 0.4, 0.4), ghost); hood.position.y = 1.7; body.add(hood);
      eye(body, -0.09, 1.7, 0.21, d.glow); eye(body, 0.09, 1.7, 0.21, d.glow);
      for (const s of [-1, 1]) { const arm = limb(body, s * 0.36, 1.5, 0); const am = new THREE.Mesh(B(0.1, 0.6, 0.1), ghost); am.position.y = -0.28; arm.add(am); wings.push(arm); }
      floats = true; tagY = 2.3; break;
    }
    case 'guardian': {
      for (const s of [-1, 1]) { const l = limb(root, s * 0.22, 0.7, 0); addMesh(l, B(0.3, 0.7, 0.32), c, 0, -0.35, 0); legs.push(l); }
      addMesh(body, B(0.9, 0.9, 0.55), c, 0, 1.15, 0);
      addMesh(body, G.dod(0.36), 0x2a2a1a, 0, 1.75, 0.04);
      const heart = new THREE.Mesh(G.oct(0.14), new THREE.MeshBasicMaterial({ color: d.glow ?? 0x7ad070 })); heart.position.set(0, 1.2, 0.29); body.add(heart);
      eye(body, -0.12, 1.8, 0.3, d.glow); eye(body, 0.12, 1.8, 0.3, d.glow);
      for (let i = 0; i < 7; i++) { const b = addMesh(body, G.cone(0.06, 0.6, 4), 0x3a5a2a, Math.cos(i * 0.9) * 0.3, 2.05, Math.sin(i * 0.9) * 0.2); b.rotation.z = Math.cos(i * 0.9) * 0.6; }
      for (const s of [-1, 1]) { const arm = limb(body, s * 0.6, 1.5, 0); addMesh(arm, B(0.24, 0.9, 0.26), c, 0, -0.42, 0); addMesh(arm, B(0.3, 0.26, 0.3), 0x2a2a1a, 0, -0.9, 0); wings.push(arm); }
      tagY = 2.6; break;
    }
    case 'beetle': {
      // a rock-eating crawler: a glossy domed shell in plates, a blunt head with mandibles, six legs
      addMesh(body, G.sphere(0.42, 14), c, 0, 0.42, -0.05).scale.set(1, 0.62, 1.35);
      for (let i = 0; i < 3; i++) addMesh(body, G.round(0.74 - i * 0.08, 0.06, 0.22, 0.03), a, 0, 0.66 - i * 0.03, -0.32 + i * 0.27);
      addMesh(body, G.round(0.04, 0.05, 0.9, 0.02), 0x1a1614, 0, 0.69, -0.05);
      const head = limb(body, 0, 0.36, 0.55);
      addMesh(head, G.round(0.34, 0.24, 0.26, 0.08), 0x3a3430, 0, 0, 0);
      for (const s of [-1, 1]) { const m = addMesh(head, G.cone(0.04, 0.24, 6), a, s * 0.1, -0.04, 0.18); m.rotation.set(1.4, 0, s * 0.5); }
      eye(head, -0.09, 0.04, 0.13, 0xffd27a); eye(head, 0.09, 0.04, 0.13, 0xffd27a);
      for (const z of [-0.3, 0, 0.3]) for (const s of [-1, 1]) {
        const l = limb(root, s * 0.36, 0.3, z);
        const seg = addMesh(l, G.round(0.06, 0.32, 0.06, 0.025), 0x2a2420, s * 0.08, -0.12, 0); seg.rotation.z = s * 0.7;
        legs.push(l);
      }
      tagY = 1.2; break;
    }
    case 'golem': {
      // an ember golem: stacked stone with fire showing through the cracks
      const glow = new THREE.MeshBasicMaterial({ color: d.glow ?? 0xff7a30 });
      const crack = (parent: THREE.Object3D, x: number, y: number, z: number, w: number, h: number) => { const m = new THREE.Mesh(G.sharp(w, h, 0.02), glow); m.position.set(x, y, z); parent.add(m); };
      for (const s of [-1, 1]) { const l = limb(root, s * 0.26, 0.62, 0); addMesh(l, G.dod(0.24), c, 0, -0.2, 0).scale.set(1, 1.3, 1); addMesh(l, G.dod(0.2), 0x2a2422, 0, -0.5, 0.03); legs.push(l); }
      addMesh(body, G.dod(0.55), c, 0, 1.2, 0).scale.set(1.15, 1, 0.85);
      addMesh(body, G.dod(0.38), 0x4a403c, 0, 1.62, -0.05);
      crack(body, -0.1, 1.2, 0.44, 0.05, 0.4); crack(body, 0.14, 1.05, 0.42, 0.04, 0.3); crack(body, 0.02, 1.36, 0.43, 0.28, 0.04);
      const core = new THREE.Mesh(G.oct(0.16), glow); core.position.set(0, 1.18, 0.38); body.add(core);
      const head = limb(body, 0, 1.92, 0.06);
      addMesh(head, G.dod(0.24), c, 0, 0, 0);
      eye(head, -0.08, 0.02, 0.2, d.glow); eye(head, 0.08, 0.02, 0.2, d.glow);
      for (const s of [-1, 1]) {
        const arm = limb(body, s * 0.72, 1.5, 0);
        addMesh(arm, G.dod(0.22), c, 0, -0.2, 0).scale.set(1, 1.4, 1);
        addMesh(arm, G.dod(0.26), 0x2a2422, 0, -0.62, 0.02);
        crack(arm, 0, -0.22, 0.2, 0.04, 0.22);
        wings.push(arm);
      }
      tagY = 2.6; break;
    }
  }
  root.scale.setScalar(d.scale);
  root.traverse((o: THREE.Object3D) => { const m = o as THREE.Mesh; if (m.isMesh) { m.castShadow = true; } });
  mergeRig(root);
  return { root, body, legs, wings, floats, tagY: tagY * d.scale };
}

/* ---------------- worn gear on characters ---------------- */
export const METAL_COL = [0, 0xc48a3f, 0xa3aab1, 0xbff0ff, 0x5a7a3a, 0x8a6a3e, 0x7d8894, 0x5ad0e8, 0xe8602a, 0xdde2e8, 0xf0c040, 0x3a5ad8, 0xc8ccd2, 0x6a6a72, 0x2a2a30, 0xc49cff, 0x8ab8b0, 0xeef0f6, 0x9a6a3e, 0xb89a74, 0x8a5a32, 0x7a7a80, 0x5e4028, 0x4a6a4e, 0x7a6450, 0xc8cce8, 0xdce8ff];

/** Draw a weapon in the hand. `id` is a gear id; looks come from the shared table. */
export function weaponMesh(model: string, metal: number): THREE.Group {
  const g = new THREE.Group(), col = METAL_COL[metal] || 0xa3aab1;
  if (model === 'sword') { addMesh(g, G.box(0.05, 0.05, 0.16), 0x5a3a20, 0, 0, 0.06); addMesh(g, G.box(0.2, 0.04, 0.04), col, 0, 0, 0.15); addMesh(g, G.box(0.06, 0.02, 0.62), col, 0, 0, 0.47); }
  else if (model === 'battleaxe') { addMesh(g, G.box(0.05, 0.05, 0.75), 0x6b4a2b, 0, 0, 0.3); addMesh(g, G.box(0.04, 0.34, 0.2), col, 0, 0.08, 0.6); }
  else if (model === 'maul') { addMesh(g, G.box(0.06, 0.06, 0.8), 0x4a3a2a, 0, 0, 0.32); addMesh(g, G.dod(0.18), col, 0, 0, 0.74); const h = new THREE.Mesh(G.oct(0.06), new THREE.MeshBasicMaterial({ color: 0x7ad070 })); h.position.set(0, 0.14, 0.74); g.add(h); }
  else if (model === 'shortbow' || model === 'longbow') {
    const len = model === 'longbow' ? 1.3 : 0.95, wood = metal === 5 ? 0x8a6a3e : col;
    const arc = new THREE.Mesh(new THREE.TorusGeometry(len / 2, 0.025, 4, 12, Math.PI * 0.8), mat(wood));
    arc.rotation.set(0, Math.PI / 2, Math.PI / 2 + Math.PI * 0.1); arc.position.set(0, 0, 0.12); g.add(arc);
    addMesh(g, G.box(0.005, 0.005, len * 0.95), 0xe8e4da, 0, 0, 0.12 - len * 0.18).rotation.x = Math.PI / 2;
  }
  return g;
}

/** Armour pieces over the base character: helm, breastplate, greaves. Metals 0 = none. */
export function armourMeshes(metals: [number, number, number]) {
  const head = new THREE.Group(), body = new THREE.Group(), legs = [new THREE.Group(), new THREE.Group()];
  const [h, b, l] = metals;
  if (h >= SEWN || b >= SEWN || l >= SEWN) sewnMeshes(metals, head, body, legs);
  if (h && h < SEWN) { addMesh(head, G.box(0.34, 0.2, 0.33), METAL_COL[h], 0, 1.36, 0); addMesh(head, G.box(0.06, 0.18, 0.04), METAL_COL[h], 0, 1.24, 0.15); }
  if (b && b < SEWN) { addMesh(body, G.box(0.48, 0.42, 0.32), METAL_COL[b], 0, 0.84, 0); addMesh(body, G.box(0.56, 0.1, 0.34), METAL_COL[b], 0, 1.04, 0); }
  if (l && l < SEWN) for (const g of legs) addMesh(g, G.box(0.19, 0.3, 0.21), METAL_COL[l], 0, -0.16, 0);
  // the pieces are drawn for the old figure; fit them to today's (smaller head higher up, a
  // longer torso, longer legs) by mapping each frame rather than redrawing every set
  head.scale.setScalar(0.8); head.position.y = 1.4 - 1.21 * 0.8;
  body.scale.set(1, 1.06, 0.95); body.position.y = 0.67 - 0.53 * 1.06;
  for (const g of legs) g.scale.set(0.95, 1.44, 0.95);
  return { head, body, legs };
}

/* ---------------- sewn leather (Crafting) ---------------- */
/** Metal numbers from here up are sewn: 18 leather, 19 furred, 20 studded, 21 wolfhide, 22 boarhide, 23 lurkerhide, 24 chitin, 25 wraithweave, 26 moonhide. */
const SEWN = 18;
const shade = (c: number, k: number) => new THREE.Color(c).multiplyScalar(k).getHex();
const STITCH = 0xe8dcc0, FUR = 0xd8c8a8, COPPER = 0xd08a4a;
/** Fur trim for each set that has it (furred, wolfhide, moonhide). */
const TRIM: Partial<Record<number, number>> = { 19: FUR, 21: 0x9a9aa0, 26: 0xf4f6ff };

/**
 * Leather is soft where metal is hard: a hood instead of a helm, a laced jerkin with a belt and
 * shoulder pieces instead of a breastplate, chaps with a knee patch instead of greaves. Each set
 * has something of its own on top: fur trim, copper studs, scales, chitin plates, trailing silk.
 */
function sewnMeshes([h, b, l]: [number, number, number], head: THREE.Group, body: THREE.Group, legs: THREE.Group[]) {
  if (h >= SEWN) {
    const c = METAL_COL[h], dark = shade(c, 0.78);
    // the hood: a rounded shell over the crown and back of the head, open at the face, with a cowl at the neck
    addMesh(head, G.round(0.36, 0.22, 0.35, 0.1), c, 0, 1.36, -0.015);
    addMesh(head, G.round(0.36, 0.3, 0.08, 0.035), c, 0, 1.24, -0.16);
    for (const x of [-0.17, 0.17]) addMesh(head, G.round(0.04, 0.26, 0.26, 0.018), c, x, 1.24, -0.01);
    addMesh(head, G.round(0.42, 0.08, 0.36, 0.035), dark, 0, 1.1, -0.01);
    // a seam over the crown
    addMesh(head, G.round(0.02, 0.012, 0.3, 0.005), STITCH, 0, 1.475, -0.02);
    const trim = TRIM[h];
    if (trim) { addMesh(head, G.round(0.38, 0.05, 0.05, 0.02), trim, 0, 1.43, 0.15); for (const x of [-0.18, 0.18]) addMesh(head, G.round(0.05, 0.28, 0.05, 0.02), trim, x, 1.27, 0.12); }
    if (h === 26) for (const x of [-0.1, 0.1]) addMesh(head, G.cone(0.05, 0.12, 4), trim ?? c, x, 1.52, -0.04);
    if (h === 25) addMesh(head, G.round(0.3, 0.2, 0.03, 0.01), shade(c, 0.9), 0, 1.06, -0.19, { transparent: true, opacity: 0.75 });
  }
  if (b >= SEWN) {
    const c = METAL_COL[b], dark = shade(c, 0.72), light = shade(c, 1.12);
    // the jerkin, longer than a tunic, with a laced front and shoulder pieces
    addMesh(body, G.round(0.47, 0.5, 0.31, 0.09), c, 0, 0.8, 0);
    addMesh(body, G.round(0.49, 0.1, 0.33, 0.04), dark, 0, 0.55, 0);
    addMesh(body, G.round(0.06, 0.42, 0.02, 0.01), dark, 0, 0.8, 0.158);
    for (let i = 0; i < 4; i++) addMesh(body, G.round(0.08, 0.012, 0.012, 0.005), STITCH, 0, 0.68 + i * 0.08, 0.17).rotation.z = i % 2 ? 0.5 : -0.5;
    for (const x of [-0.27, 0.27]) addMesh(body, G.round(0.16, 0.07, 0.3, 0.03), light, x, 1.03, 0).rotation.z = x > 0 ? -0.35 : 0.35;
    addMesh(body, G.round(0.5, 0.06, 0.34, 0.02), 0x3a2618, 0, 0.62, 0);
    addMesh(body, G.round(0.07, 0.06, 0.03, 0.01), 0xb8a070, 0.12, 0.62, 0.17);
    const trim = TRIM[b];
    if (trim) { addMesh(body, G.round(0.42, 0.09, 0.32, 0.04), trim, 0, 1.06, 0); addMesh(body, G.round(0.5, 0.06, 0.34, 0.03), trim, 0, 0.5, 0); }
    if (b === 20) for (const y of [0.72, 0.86, 0.98]) for (const x of [-0.16, -0.08, 0.08, 0.16]) addMesh(body, G.sphere(0.017, 6), COPPER, x, y, 0.158);
    if (b === 23) for (let r = 0; r < 4; r++) for (let k = 0; k < 4; k++) { const sc = addMesh(body, G.oct(0.035), light, -0.15 + k * 0.1 + (r % 2) * 0.05, 0.7 + r * 0.08, 0.152); sc.scale.set(1, 1.2, 0.3); }
    if (b === 24) for (let r = 0; r < 4; r++) addMesh(body, G.round(0.43 - r * 0.02, 0.07, 0.04, 0.02), r % 2 ? shade(c, 1.2) : shade(c, 0.95), 0, 0.68 + r * 0.1, 0.15).rotation.x = -0.15;
    if (b === 25) for (const x of [-0.16, -0.05, 0.06, 0.17]) addMesh(body, G.round(0.09, 0.26, 0.02, 0.01), light, x, 0.42, 0.13 - Math.abs(x) * 0.2, { transparent: true, opacity: 0.8 }).rotation.x = 0.08;
  }
  if (l >= SEWN) {
    const c = METAL_COL[l], dark = shade(c, 0.75);
    for (const g of legs) {
      // chaps: over the trousers, a darker knee patch with stitching, a fold at the boot top
      addMesh(g, G.capsule(0.088, 0.26), c, 0, -0.19, 0);
      addMesh(g, G.round(0.13, 0.11, 0.03, 0.02), dark, 0, -0.24, 0.085);
      addMesh(g, G.round(0.12, 0.01, 0.01, 0.004), STITCH, 0, -0.18, 0.1);
      addMesh(g, G.round(0.19, 0.05, 0.2, 0.02), dark, 0, -0.36, 0);
      const trim = TRIM[l];
      if (trim) addMesh(g, G.round(0.2, 0.06, 0.21, 0.025), trim, 0, -0.35, 0);
      if (l === 20) for (const y of [-0.08, -0.14]) addMesh(g, G.sphere(0.015, 6), COPPER, 0, y, 0.09);
      if (l === 24) addMesh(g, G.round(0.15, 0.16, 0.05, 0.03), shade(c, 1.2), 0, -0.1, 0.07);
    }
  }
}
